import { getEventUrl } from 'constants/pages';
import { withEngagementSource } from 'constants/payloads/club/engagementBeacon';
import {
  GetEventDetailsQuery,
  GetUserEventRegistrationQuery,
  TeamTypesEnum,
} from 'types/generated/client';
import type { Event } from '../../types';

type EventTeamMemberFromDetails = NonNullable<
  NonNullable<GetEventDetailsQuery['eventsByPk']>['groups']
>[number]['teams'][number]['members'][number];

export type UserNeedingPartner = Pick<EventTeamMemberFromDetails, 'userId' | 'userProfile'>;

/**
 * Returns users in the same event group who need a partner (teams with 1 member).
 * Excludes the logged-in user.
 */
export function getUsersNeedingPartnerForGroup(
  event: Event,
  groupId: string,
  excludeUserId: string | null | undefined,
): UserNeedingPartner[] {
  const group = event.groups?.find((g) => g.id === groupId);
  if (!group?.teams) return [];

  const members = group.teams
    .filter((team) => team.members?.length === 1)
    .flatMap((team) => team.members ?? [])
    .filter((m): m is NonNullable<typeof m> => !!m && m.userId !== excludeUserId);

  const seen = new Set<string>();
  return members.filter((m) => {
    if (!m.userId || seen.has(m.userId)) return false;
    seen.add(m.userId);
    return true;
  });
}

export interface DescriptionTextParams {
  hasPreviousEventRegistration: boolean;
  hasPreviousGroupRegistration: boolean;
  eventGroupRegistrations: GetUserEventRegistrationQuery['eventGroupRegistrations'];
  hasPartners?: boolean;
}

export function getDescriptionText({
  hasPreviousEventRegistration,
  hasPreviousGroupRegistration,
  eventGroupRegistrations,
  hasPartners = true,
}: DescriptionTextParams) {
  if (hasPreviousEventRegistration && !hasPreviousGroupRegistration) {
    return 'You have registered for the tournament but no events. Add events below.';
  }

  const hasMissingPartner =
    hasPartners &&
    eventGroupRegistrations.some((egr) => {
      const teamMembers = egr.group.teams.flatMap((team) => team.members);
      return egr.group.teamType === TeamTypesEnum.Doubles && teamMembers.length === 1;
    });

  if (hasMissingPartner) {
    return 'You have registered alone for an event that requires a partner.';
  }
  return 'You registered successfully.';
}

export function buildShareUrl(eventId: string, teamId?: string, invitationId?: string): string {
  const baseUrl = `${process.env.ROOT_URL}${getEventUrl(eventId)}`;
  const queryParams: string[] = [];
  if (teamId) queryParams.push(`team=${teamId}`);
  if (invitationId) queryParams.push(`invite=${invitationId}`);
  const url = queryParams.length > 0 ? `${baseUrl}?${queryParams.join('&')}` : baseUrl;
  return withEngagementSource(url, 'SHARED_LINK');
}
